import { Users, Phone, Calendar, TrendingUp, BarChart2, Activity, Clock, CheckCircle2 } from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { StatCard } from '@/components/ui/Card';
import { useLeadStats } from '@/hooks/useLeads';
import { formatNumber } from '@/utils/formatters';
import type { DashboardStats } from '@/types';

const SOURCE_COLORS = {
  meta: '#1877F2',
  google: '#EA4335',
  seo: '#10B981',
  gmb: '#F59E0B',
  referral: '#8B5CF6',
  walk_in: '#14B8A6',
  call: '#F97316',
  website: '#06B6D4',
  other: '#94A3B8',
};

export function SuperAdminDashboard() {
  const { data: stats, isLoading } = useLeadStats();
  const typedStats = stats as DashboardStats | undefined;

  const sourceData = typedStats?.sourceStats?.map(s => ({
    name: (s.source ?? 'Unknown').toUpperCase(),
    value: Number(s.count),
    color: SOURCE_COLORS[s.source as keyof typeof SOURCE_COLORS] ?? '#94A3B8',
  })) ?? [];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl h-32 border border-slate-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">HeptaCare Health CRM Overview</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-white rounded-lg px-3 py-2 border border-slate-200">
          <Activity size={14} className="text-green-500" />
          Live data
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Leads"
          value={formatNumber(typedStats?.total)}
          icon={<Users size={22} />}
          color="blue"
        />
        <StatCard
          title="New Today"
          value={formatNumber(typedStats?.new_leads)}
          icon={<TrendingUp size={22} />}
          color="cyan"
        />
        <StatCard
          title="Follow-ups"
          value={formatNumber(typedStats?.followup)}
          icon={<Clock size={22} />}
          color="yellow"
        />
        <StatCard
          title="OPD Done"
          value={formatNumber(typedStats?.opd)}
          icon={<Calendar size={22} />}
          color="green"
        />
        <StatCard
          title="IPD Done"
          value={formatNumber(typedStats?.ipd)}
          icon={<Activity size={22} />}
          color="purple"
        />
        <StatCard
          title="Insurance Cases"
          value={formatNumber(typedStats?.insurance)}
          icon={<BarChart2 size={22} />}
          color="orange"
        />
        <StatCard
          title="Closed Won"
          value={formatNumber(typedStats?.closed_won)}
          icon={<CheckCircle2 size={22} />}
          color="green"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Sources Pie Chart */}
        <div className="bg-white rounded-xl shadow-card border border-slate-100 p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Leads by Source</h3>
          {sourceData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [formatNumber(value), 'Leads']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 flex-1">
                {sourceData.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
                      <span className="text-slate-600">{s.name}</span>
                    </div>
                    <span className="font-semibold text-slate-900">{formatNumber(s.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
              No data available
            </div>
          )}
        </div>

        {/* Conversion Funnel */}
        <div className="bg-white rounded-xl shadow-card border border-slate-100 p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Lead Conversion Funnel</h3>
          <div className="space-y-3">
            {[
              { label: 'Total Leads', value: typedStats?.total ?? 0, color: '#06B6D4', pct: 100 },
              { label: 'OPD Done', value: typedStats?.opd ?? 0, color: '#10B981', pct: typedStats?.total ? (typedStats.opd / typedStats.total) * 100 : 0 },
              { label: 'IPD Done', value: typedStats?.ipd ?? 0, color: '#8B5CF6', pct: typedStats?.total ? (typedStats.ipd / typedStats.total) * 100 : 0 },
              { label: 'Closed Won', value: typedStats?.closed_won ?? 0, color: '#1E3A5F', pct: typedStats?.total ? (typedStats.closed_won / typedStats.total) * 100 : 0 },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">{item.label}</span>
                  <span className="font-semibold text-slate-900">{formatNumber(item.value)}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(item.pct, 100)}%`, backgroundColor: item.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
